import { useContext } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueries } from '@tanstack/react-query';

import {
  BodyWrapper,
  Header,
  Loader,
  MainWrapper,
  WelcomeElement,
} from '@components';
import { GlobalContext } from '@contexts';
import {
  ChevronRightIcon,
} from '@svgs';
import { businessesQuery, sitesQuery } from '@queries';

import { MainSidebar } from '../components/MainSidebar/MainSidebar.jsx';

import { ProjectCard } from './ProjectCard/ProjectCard.jsx';
import { ProjectRow } from './ProjectRow/ProjectRow.jsx';

const Projects = () => {
  const { t, i18n } = useTranslation();
  const globals = useContext(GlobalContext);

  const {
    data: businessesData,
    isPending: businessesIsPending,
    isError,
  } = useQuery(
    businessesQuery({
      deleted: 0,
      order_by: 'business_name',
      project_type: 'default',
      limit: 20,
    }),
  );
  const businessID = businessesData?.objects?.[0]?.id;
  const shouldShowGrid = businessesData?.objects?.length <= 10;

  const businessIds = businessesData?.objects?.map((business) => business.id) || [];

  // Fetch the first site for each business to show thumbnails per card
  const siteQueries = useQueries({
    queries: shouldShowGrid
      ? businessIds.map((id) => ({
          ...sitesQuery(
            {
              business_id: id,
              deleted: 0,
              limit: 1,
              order_by: 'base_directory',
              type: 'default',
            },
            {
              enabled: shouldShowGrid && businessIds.length > 0,
            },
          ),
        }))
      : [],
  });

  const siteThumbnails = siteQueries.reduce((acc, query, index) => {
    const businessId = businessIds[index];
    const thumb = query.data?.objects?.[0]?.screenshot_thumbnail;
    if (businessId) {
      acc[businessId] = thumb;
    }
    return acc;
  }, {});

  const sitesAreFetching =
    shouldShowGrid && siteQueries.some((query) => query.fetchStatus === 'fetching');

  const isPending = businessesIsPending || sitesAreFetching;

  if (
    !globals.isLoaded ||
    (globals.isLoaded && !globals.visible.projectsView && !businessesData)
  ) {
    if (isError) {
      return (
        <>
          <Header />
          <MainWrapper>Account does not have access to projects</MainWrapper>
        </>
      );
    }
    return (
      <>
        <Header />
        <div className="sb-main">
          <div className="sb-flex">
            <Loader className="sb-loader sb-loader-full" />
          </div>
        </div>
      </>
    );
  }

  if (businessesData && globals.isLoaded && !globals.visible.projectsView) {
    return (
      <Navigate
        to={`/dashboard/projects/${businessID}/sites`}
        replace
      />
    );
  }

  return (
    <>
      <Header />
      <BodyWrapper>
        <MainSidebar />
        <MainWrapper>
          <div className="sb-main-breadcrumbs">
            <ul className="sb-flex">
              <li className="sb-flex">
                <a href="">Projects</a>
                <ChevronRightIcon />
              </li>
            </ul>
          </div>
          <div className="sb-manage-content">
            <div className="sb-manage-content-head">
              <h1>{t('Projects')}</h1>
              <div className="sb-button-list-inline">
                {globals.perms.Business.includes('add') && (
                  <Link
                    to={`/dashboard/projects/new`}
                    className="sb-btn sb-btn-cancel"
                  >
                    Add new
                  </Link>
                )}
              </div>
            </div>
            {isPending && (
              <Loader className="sb-loader sb-loader-large" />
            )}
            {!isPending &&
              (isError || businessesData.objects?.length == 0) && (
                <WelcomeElement>
                  <h1>Welcome to projects</h1>
                  <p>
                    Projects are the group of Websites, Locations and associated
                    Templates for your Brand and domain.
                  </p>
                  <Link
                    to={`/dashboard/projects/new`}
                    className="sb-btn sb-btn-save"
                  >
                    Add first project
                  </Link>
                </WelcomeElement>
              )}
            {!isPending &&
              !isError &&
              shouldShowGrid && (
                <div className="sb-grid sb-websites-wrap">
                  {businessesData.objects.map((business) => {
                    return (
                      <ProjectCard
                        key={business.id}
                        business={business}
                        siteThumbnail={siteThumbnails[business.id]}
                      />
                    );
                  })}
                  {/* below elements needed to fill the grid */}
                  <div className="sb-website-item"></div>
                  <div className="sb-website-item"></div>
                  <div className="sb-website-item"></div>
                </div>
              )}
            {!isPending &&
              !isError &&
              !shouldShowGrid && (
                <div className="sb-content-view-row">
                  {businessesData.objects.map((business) => {
                    return <ProjectRow key={business.id} business={business} />;
                  })}
                </div>
              )}
          </div>
        </MainWrapper>
      </BodyWrapper>
    </>
  );
};

export default Projects;
